import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, LogOut, Sparkles, MoreHorizontal, Sun, Moon, Settings } from 'lucide-react';
import { CurrencySelector } from './CurrencySelector';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { useUpgradePrompt } from '../../context/UpgradePromptContext';
import { UPGRADE_CTA } from '../../config/limits';
import { useTheme } from '../../context/ThemeContext';
import logoHeaderDark from '../../assets/brand/logo-header-dark.svg';
import logoHeaderLight from '../../assets/brand/logo-header-light.svg';

interface HeaderProps {
  lastUpdated: Date | null;
  loading: boolean;
  onRefresh: () => void;
}

interface HeaderMenuProps {
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  onRefresh: () => void;
  loading: boolean;
  showUpgrade: boolean;
  onUpgrade: () => void;
  userEmail: string | null;
  onSettings: () => void;
  onLogout: () => void;
}

/**
 * Mobile-only ("…") overflow menu holding the secondary header controls. Mirrors
 * the app's existing dropdown pattern (toggle button + fixed inset-0 outside-click
 * backdrop + absolute right-0 panel, slate/overlay tokens) from RowActions /
 * CurrencySelector. Every item calls the SAME handler as its desktop control.
 */
function HeaderMenu({
  theme, onToggleTheme, onRefresh, loading,
  showUpgrade, onUpgrade, userEmail, onSettings, onLogout,
}: HeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const itemClass =
    'w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50';

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="More options"
        aria-expanded={open}
        className="flex items-center justify-center w-[34px] h-[34px] bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors text-slate-400 hover:text-slate-200"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {open && (
        <>
          {/* Outside-click backdrop */}
          <div className="fixed inset-0 z-40" onClick={close} />
          <div className="absolute right-0 top-full mt-1.5 z-50 w-56 rounded-xl overflow-hidden shadow-2xl bg-slate-800 border border-slate-700">
            {userEmail && (
              <div className="px-3 py-2 border-b border-slate-700">
                <p className="text-xs text-slate-400 truncate" title={userEmail}>{userEmail}</p>
              </div>
            )}

            <button type="button" onClick={() => { close(); onSettings(); }} className={itemClass}>
              <Settings className="w-4 h-4" />
              Settings
            </button>

            {showUpgrade && (
              <button
                type="button"
                onClick={() => { close(); onUpgrade(); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left font-medium hover:bg-slate-700 transition-colors"
                style={{ color: '#d4b483' }}
              >
                <Sparkles className="w-4 h-4" />
                Upgrade
              </button>
            )}

            <button type="button" onClick={() => { close(); onToggleTheme(); }} className={itemClass}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === 'dark' ? 'Light mode' : 'Dark mode'}
            </button>

            <button type="button" onClick={() => { close(); onRefresh(); }} disabled={loading} className={itemClass}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {userEmail && (
              <button
                type="button"
                onClick={() => { close(); onLogout(); }}
                className={`${itemClass} border-t border-slate-700`}
              >
                <LogOut className="w-4 h-4" />
                Log out
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function Header({ lastUpdated, loading, onRefresh }: HeaderProps) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { tier, loading: subLoading } = useSubscription();
  const { open: openUpgradePrompt } = useUpgradePrompt();
  const { theme, toggle: toggleTheme } = useTheme();

  // Show the upgrade CTA once we know a logged-in user is not already on the top
  // tier — so it keeps pointing somewhere for a Plus subscriber, who still has an
  // upgrade available.
  const showUpgrade = !!user && !subLoading && tier !== 'pro';

  const timeStr = lastUpdated
    ? lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    : null;

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <header className="border-b border-slate-700 bg-slate-950/95 backdrop-blur-sm sticky top-0 z-30">
      <div className="px-4 sm:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo: the horizontal lockup in the current theme. 28px tall below
              sm (about 159px wide, leaving room for the currency selector and
              the menu button on a 320px phone), 32px from sm up. width / height
              carry the 802 x 141.6 ratio, so nothing shifts while it loads. */}
          <div className="flex items-center min-w-0">
            <img
              src={theme === 'light' ? logoHeaderLight : logoHeaderDark}
              alt="MyCryptoPlanner"
              width={181}
              height={32}
              className="block h-7 sm:h-8 w-auto flex-shrink-0"
            />
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {timeStr && (
              <span className="label-caps text-slate-600 hidden md:block">
                Updated {timeStr}
              </span>
            )}

            {/* Currency selector — always visible */}
            <CurrencySelector />

            {/* Secondary controls — inline on ≥ sm, collapsed into the "…" menu below on mobile */}
            <div className="hidden sm:flex items-center gap-3">
            {/* Theme toggle */}
            <ThemeToggle />

            {/* Refresh */}
            <button
              onClick={onRefresh}
              disabled={loading}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50 bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded-lg"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* Upgrade (logged-in, on a tier below Pro). Deliberately NOT "Upgrade to
                Pro": for a Plus subscriber that names the wrong next step, and the
                modal names the tier anyway. */}
            {showUpgrade && (
              <button
                onClick={() => openUpgradePrompt(UPGRADE_CTA)}
                title="Upgrade your plan"
                className="flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg transition-transform hover:scale-[1.02]"
                style={{ backgroundColor: '#d4b483', color: '#0c0e12' }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Upgrade</span>
              </button>
            )}

            {/* Account: email + settings + log out */}
            {user && (
              <div className="flex items-center gap-3 pl-3 ml-1 border-l border-slate-700">
                <span className="label-caps text-slate-400 hidden lg:block normal-case tracking-normal">
                  {user.email}
                </span>
                <button
                  onClick={() => navigate('/settings')}
                  className="flex items-center justify-center w-[34px] h-[34px] text-slate-400 hover:text-slate-200 transition-colors bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg"
                  title="Settings"
                  aria-label="Settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded-lg"
                  title="Log out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Log out</span>
                </button>
              </div>
            )}
            </div>

            {/* Mobile: secondary controls collapse into a "…" overflow menu */}
            <div className="sm:hidden">
              <HeaderMenu
                theme={theme}
                onToggleTheme={toggleTheme}
                onRefresh={onRefresh}
                loading={loading}
                showUpgrade={showUpgrade}
                onUpgrade={() => openUpgradePrompt(UPGRADE_CTA)}
                userEmail={user?.email ?? null}
                onSettings={() => navigate('/settings')}
                onLogout={handleLogout}
              />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
